import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export type ApprovalStatus = "Draft" | "PendingApproval" | "Approved" | "Rejected";

export interface Batch {
  id: string;
  inputId: string;
  inputName?: string;
  inputCode?: string;
  batchNumber: string;
  lotNumber: string;
  receivedQuantity: number;
  currentQuantity?: number;
  reservedQuantity?: number;
  receivedDate: string;
  manufactureDate: string;
  expiryDate: string;
  unitCost: number;
  unitOfMeasure?: number | string;
  packSize?: number | null;
  numberOfPacks?: number | null;
  commercialQuantity?: number | null;
  commercialUnit?: string | null;
  commercialUnitCost?: number | null;
  commercialSubtotal?: number | null;
  costingReviewRequired?: boolean;
  purchaseOrderNumber: string;
  invoiceNumber: string;
  isActive: boolean;
  createdAt: string;
  supplierId?: string;
  supplierName?: string;
  /** GM stock-receipt approval gate (SPEC §e). Pending batches show with a
   * Pending badge and are not selectable for draw/issue/requisition flows. */
  approvalStatus?: ApprovalStatus;
  approvedByUserId?: string | null;
  approvedAt?: string | null;
  rejectionReason?: string | null;
}

export interface CreateBatchRequest {
  inputId: string;
  batchNumber: string;
  lotNumber: string;
  receivedQuantity: number;
  receivedDate: string;
  manufactureDate: string;
  expiryDate: string;
  unitCost: number;
  purchaseOrderNumber: string;
  invoiceNumber: string;
}

export interface UpdateBatchRequest {
  id: string;
  lotNumber: string;
  manufactureDate: string;
  expiryDate: string;
  purchaseOrderNumber: string;
  invoiceNumber: string;
  isActive: boolean;
}

export interface ExpiringBatch extends Batch {
  daysUntilExpiry: number;
}

export const batchesApi = {
  getBatchesByInput: async (inputId: string): Promise<Batch[]> => {
    const { data } = await apiClient.get<ApiResponse<Batch[]>>(
      `/Stock/batches/input/${inputId}`
    );
    return data.data;
  },

  getExpiringBatches: async (daysAhead: number = 30): Promise<ExpiringBatch[]> => {
    const { data } = await apiClient.get<ApiResponse<ExpiringBatch[]>>(
      `/Stock/batches/expiring?daysAhead=${daysAhead}`
    );
    return data.data;
  },

  getExpiredBatches: async (): Promise<Batch[]> => {
    const { data } = await apiClient.get<ApiResponse<Batch[]>>(
      '/Stock/batches/expired'
    );
    return data.data;
  },

  createBatch: async (payload: CreateBatchRequest): Promise<Batch> => {
    const { data } = await apiClient.post<ApiResponse<Batch>>(
      '/Stock/batches',
      payload
    );
    return data.data;
  },

  updateBatch: async (
    id: string,
    payload: UpdateBatchRequest
  ): Promise<Batch> => {
    const { data } = await apiClient.put<ApiResponse<Batch>>(
      `/Stock/batches/${id}`,
      payload
    );
    return data.data;
  },

  deleteBatch: async (id: string): Promise<void> => {
    await apiClient.delete(`/Stock/batches/${id}`);
  },
};
